import './SupportFeedbackBar.scss';
import { useEffect, useState } from 'react';
import { CloseButton } from '@mantine/core';
import { MdCheckCircleOutline, MdOutlineThumbDown, MdOutlineThumbUp, MdThumbDown, MdThumbUp } from 'react-icons/md';
import Button from '@srm/ui/button';
import SupportBar from '@molecules/SupportBar';
import { joinNames } from '@srm/ui/lib/chatText';
import type { SupportFeedbackRating } from '@models/supportAccess/supportAccess.types';
import { announceSupport } from '@providers/ScreenShareProvider/supportAnnouncer';
import {
    SUPPORT_FEEDBACK_COMMENT_LIMIT,
    SUPPORT_FEEDBACK_ERROR_TEXT,
    SupportFeedbackError,
} from '@providers/ScreenShareProvider/supportFeedback';

export interface ISupportFeedbackBarProps {
    /** Специалисты, которые подключались к показу */
    names: readonly string[];
    /** Длительность показа, мин */
    minutes: number;
    /** Оценка принята: «Спасибо» */
    sent?: boolean;
    /** Отправить оценку; отказ — исключение (плашка показывает его и даёт повторить) */
    onSend: (rating: SupportFeedbackRating, comment: string) => Promise<void>;
    /** Пользователь начал оценивать: плашка больше не уходит сама */
    onHold?: () => void;
    onDismiss: () => void;
    /** Оценка выбрана с самого начала (витрина) */
    defaultRating?: SupportFeedbackRating;
    /** Полоса без места в оболочке */
    floating?: boolean;
}

const RATINGS: { value: SupportFeedbackRating; label: string; icon: typeof MdThumbUp; chosen: typeof MdThumbUp }[] = [
    { value: 'up', label: 'Помогла', icon: MdOutlineThumbUp, chosen: MdThumbUp },
    { value: 'down', label: 'Не помогла', icon: MdOutlineThumbDown, chosen: MdThumbDown },
];

/**
 * Оценка пользователя после конца показа экрана (IMPLEMENTATION.md §33, prototype-stage5.html «Пользователь: оценка»):
 * «Помогла ли поддержка?» — 👍/👎, необязательный комментарий, «Отправить», «Закрыть». Тем же языком, что полоса показа.
 */
const SupportFeedbackBar = ({
    names,
    minutes,
    sent = false,
    onSend,
    onHold,
    onDismiss,
    defaultRating,
    floating,
}: ISupportFeedbackBarProps) => {
    const [rating, setRating] = useState<SupportFeedbackRating | undefined>(defaultRating);
    const [comment, setComment] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        announceSupport(sent ? 'Спасибо за оценку' : 'Показ экрана завершён. Помогла ли поддержка?');
    }, [sent]);

    const choose = (value: SupportFeedbackRating) => {
        setRating(value);
        setError('');
        onHold?.();
    };
    const send = async () => {
        if (!rating || sending) return;
        setSending(true);
        setError('');
        try {
            await onSend(rating, comment.trim());
        } catch (reason) {
            setError(reason instanceof SupportFeedbackError ? reason.message : SUPPORT_FEEDBACK_ERROR_TEXT);
        } finally {
            setSending(false);
        }
    };

    const close = <CloseButton size="md" aria-label="Закрыть" title="Закрыть" onClick={onDismiss} />;
    if (sent)
        return (
            <SupportBar
                tone="view"
                icon={<MdCheckCircleOutline size={20} />}
                title="Спасибо за оценку!"
                description="Она попадёт в историю сеанса поддержки."
                wrap
                actions={close}
                label="Оценка показа экрана"
                live={false}
                floating={floating}
                data-testid="support-feedback-bar"
            />
        );

    const details = rating && (
        <div className="support-feedback-bar__details">
            <textarea
                className="support-feedback-bar__comment"
                rows={2}
                value={comment}
                maxLength={SUPPORT_FEEDBACK_COMMENT_LIMIT}
                placeholder="Комментарий, если хотите"
                aria-label="Комментарий к оценке"
                onChange={(event) => setComment(event.target.value)}
            />
            {(comment.length > SUPPORT_FEEDBACK_COMMENT_LIMIT - 100 || error) && (
                <span className="support-feedback-bar__note">
                    {error && (
                        <span className="support-feedback-bar__error" role="alert">
                            {error}
                        </span>
                    )}
                    {comment.length > SUPPORT_FEEDBACK_COMMENT_LIMIT - 100 && (
                        <span className="support-feedback-bar__counter">
                            {comment.length} / {SUPPORT_FEEDBACK_COMMENT_LIMIT}
                        </span>
                    )}
                </span>
            )}
        </div>
    );

    return (
        <SupportBar
            tone="view"
            title={
                <>
                    Показ экрана завершён.
                    <span className="support-feedback-bar__question"> Помогла ли поддержка?</span>
                </>
            }
            description={`${joinNames([...names])} · ${minutes} мин`}
            wrap
            details={details || undefined}
            actions={
                <>
                    <span className="support-feedback-bar__ratings" role="group" aria-label="Оценка">
                        {RATINGS.map(({ value, label, icon: Icon, chosen: Chosen }) => (
                            <button
                                key={value}
                                type="button"
                                className="support-feedback-bar__thumb"
                                data-rating={value}
                                aria-pressed={rating === value}
                                aria-label={label}
                                title={label}
                                onClick={() => choose(value)}
                            >
                                {rating === value ? <Chosen size={20} /> : <Icon size={20} />}
                            </button>
                        ))}
                    </span>
                    {rating && (
                        <Button size="sm" loading={sending} onClick={() => void send()}>
                            Отправить
                        </Button>
                    )}
                    {close}
                </>
            }
            label="Оценка показа экрана"
            live={false}
            floating={floating}
            data-testid="support-feedback-bar"
        />
    );
};

export default SupportFeedbackBar;
